import AddCircleIcon from "@mui/icons-material/AddCircle";
import { Typography } from "@mui/material";
import { styled } from "@mui/material/styles";

import { ProductAdditionListNode } from "../../apiHooks/useProduct";
import { BulletedList } from "../../components/BulletedList";
import { ReadMoreContainer } from "../../components/ExpandableContent";
import { LabeledSection } from "../../components/LabeledSection";
import { useNextTranslation } from "../../hooks";
import { usePriceFormatter } from "../../hooks/usePriceFormatter";

export type ProductSectionAdditionsProps = {
    additions: ProductAdditionListNode[];
};

export function ProductSectionAdditions({ additions }: ProductSectionAdditionsProps) {
    const [t] = useNextTranslation("product");
    const additionDescription = t("description.additions");
    const title = t("label.extraAvailable");
    return (
        <LabeledSection title={title} Icon={AddCircleIcon}>
            <ReadMoreContainer>
                <Typography>{additionDescription}</Typography>

                <BulletedList>
                    {additions.map((x, i) => (
                        <ProductAdditionEntry {...x} key={i} />
                    ))}
                </BulletedList>
            </ReadMoreContainer>
        </LabeledSection>
    );
}

const EntryWrapper = styled("li")(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "auto min-content",
    gap: theme.spacing(0, 0),
    gridTemplateAreas: '"name price" "description price"',
}));

const Description = styled(Typography)(({ theme }) => ({
    "&": {
        color: theme.palette.text.secondary,
        gridArea: "description",
    },
}));

function ProductAdditionEntry({ name, description, unitPrice }: ProductAdditionListNode) {
    const priceFormatter = usePriceFormatter(unitPrice?.currency);
    return (
        <EntryWrapper>
            <Typography style={{ gridArea: "name" }}>{name}</Typography>
            <Description variant="caption">{description}</Description>
            {unitPrice?.gross ? (
                <Typography style={{ gridArea: "price" }}>{priceFormatter(unitPrice.gross)}</Typography>
            ) : undefined}
        </EntryWrapper>
    );
}
